// F-095 Annuaire des artisans.
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { del, patch, post, upload } from "@core/api";
import { date, money } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { Button, Card, Empty, ErrorBox, FileDrop, Input, KeyValues, Loading, MoneyInput, Notice, PageHeader, Select, Table, Textarea, fieldError } from "@core/ui";
import { Status, TRADES, docUrl, usePropertyOptions, useRun } from "./shared";

const TRADE_OPTIONS = Object.entries(TRADES).map(([value, label]) => ({ value, label }));

export function ContractorsPage() {
  const org = useOrg();
  const [filter, setFilter] = useState({ trade: "", zone: "", q: "", insurance: "" });
  const params = new URLSearchParams(Object.entries(filter).filter(([, v]) => v) as [string, string][]).toString();
  const q = useGet<any>(org.api(`/pro/contractors${params ? `?${params}` : ""}`));
  return (
    <div className="page">
      <PageHeader
        eyebrow="Professionnels"
        title="Annuaire des artisans"
        intro="Les artisans avec qui votre espace travaille : métiers, zones d’intervention, attestation d’assurance décennale et interventions par logement. Cet annuaire reste interne à votre espace."
      />
      <Notice>Une attestation d’assurance couvre une période : l’état affiché dépend de la date de fin lue sur la pièce déposée et ne prouve pas la couverture actuelle. Redemandez l’attestation avant un chantier.</Notice>
      <Card title="Rechercher">
        <div className="form-row">
          <Select label="Métier" value={filter.trade} options={[{ value: "", label: "Tous" }, ...TRADE_OPTIONS]} onChange={(e) => setFilter({ ...filter, trade: e.target.value })} />
          <Input label="Zone (code postal ou ville)" value={filter.zone} onChange={(e) => setFilter({ ...filter, zone: e.target.value })} />
          <Input label="Nom" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} />
          <Select
            label="Attestation"
            value={filter.insurance}
            options={[
              { value: "", label: "Toutes" },
              { value: "a_jour", label: "À jour" },
              { value: "a_renouveler", label: "À renouveler" },
              { value: "expiree", label: "Expirée" },
              { value: "non_renseignee", label: "Non renseignée" },
            ]}
            onChange={(e) => setFilter({ ...filter, insurance: e.target.value })}
          />
        </div>
        {q.isLoading ? <Loading /> : null}
        {q.data && !q.data.items.length ? <Empty title="Aucun artisan">Ajoutez les artisans avec qui vous travaillez.</Empty> : null}
        {q.data?.items.length ? (
          <Table caption="Artisans">
            <thead>
              <tr>
                <th>Entreprise</th>
                <th>Métier</th>
                <th>Zones</th>
                <th>Contact</th>
                <th>Assurance décennale</th>
                <th className="num">Interventions</th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((c: any) => (
                <tr key={c.id}>
                  <td>
                    <Link to={org.to(`professionnels/artisans/${c.id}`)}>{c.name}</Link>
                  </td>
                  <td>{TRADES[c.trade] ?? c.trade}</td>
                  <td>{(c.zones as string[]).join(", ") || "—"}</td>
                  <td>
                    {c.phone ?? ""}
                    {c.email ? <span className="block small">{c.email}</span> : null}
                  </td>
                  <td>
                    <Status kind="insurance" value={c.insurance_status} />
                    {c.decennale_valid_until ? <span className="block small muted">Fin : {date(c.decennale_valid_until)}</span> : null}
                  </td>
                  <td className="num">{c.interventions}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
      </Card>
      {org.can("operate") ? <ContractorForm /> : null}
    </div>
  );
}

function ContractorForm({ contractor }: { contractor?: any }) {
  const org = useOrg();
  const invalidate = useInvalidate();
  const navigate = useNavigate();
  const { busy, error, run } = useRun();
  const [form, setForm] = useState({
    name: contractor?.name ?? "",
    trade: contractor?.trade ?? "",
    zones: (contractor?.zones ?? []).join(", "),
    siret: contractor?.siret ?? "",
    contact_name: contractor?.contact_name ?? "",
    email: contractor?.email ?? "",
    phone: contractor?.phone ?? "",
    address: contractor?.address ?? "",
    insurer: contractor?.insurer ?? "",
    policy_number: contractor?.policy_number ?? "",
    decennale_valid_until: contractor?.decennale_valid_until ?? "",
    internal_notes: contractor?.internal_notes ?? "",
    commercial_relationship: contractor?.commercial_relationship ?? "",
  });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });
  return (
    <Card title={contractor ? "Modifier la fiche" : "Ajouter un artisan"}>
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          const body = {
            ...form,
            zones: form.zones.split(/[,;]/).map((z: string) => z.trim()).filter(Boolean),
            decennale_valid_until: form.decennale_valid_until || null,
            clear_decennale_date: !!contractor && !form.decennale_valid_until,
          };
          const r: any = contractor
            ? await run(() => patch(org.api(`/pro/contractors/${contractor.id}`), body), "Fiche enregistrée.")
            : await run(() => post(org.api("/pro/contractors"), body), "Artisan ajouté.");
          if (r) {
            invalidate(org.api("/pro"));
            if (!contractor) navigate(org.to(`professionnels/artisans/${r.id}`));
          }
        }}
      >
        <div className="form-row">
          <Input label="Entreprise" required value={form.name} error={fieldError(error, "name")} onChange={set("name")} />
          <Select label="Métier" value={form.trade} error={fieldError(error, "trade")} options={[{ value: "", label: "Choisir" }, ...TRADE_OPTIONS]} onChange={set("trade")} />
          <Input label="Zones d’intervention" value={form.zones} help="Codes postaux ou villes, séparés par des virgules." onChange={set("zones")} />
        </div>
        <div className="form-row">
          <Input label="Contact" value={form.contact_name} onChange={set("contact_name")} />
          <Input label="Téléphone professionnel" type="tel" value={form.phone} onChange={set("phone")} />
          <Input label="E-mail professionnel" type="email" value={form.email} error={fieldError(error, "email")} onChange={set("email")} />
        </div>
        <div className="form-row">
          <Input label="SIRET" inputMode="numeric" value={form.siret} error={fieldError(error, "siret")} onChange={set("siret")} />
          <Input label="Adresse" value={form.address} onChange={set("address")} />
        </div>
        <div className="form-row">
          <Input label="Assureur (décennale)" value={form.insurer} onChange={set("insurer")} />
          <Input label="Numéro de contrat" value={form.policy_number} onChange={set("policy_number")} />
          <Input label="Fin de validité de l’attestation" type="date" value={form.decennale_valid_until} onChange={set("decennale_valid_until")} />
        </div>
        <Textarea label="Notes internes" value={form.internal_notes} help="Visibles des seuls membres de l’espace, jamais des mandants." onChange={set("internal_notes")} />
        <Input label="Relation commerciale" value={form.commercial_relationship} help="Remise, contrat cadre, lien d’intérêt : à indiquer explicitement." onChange={set("commercial_relationship")} />
        <ErrorBox error={error && !(error as any).fields?.length ? error : null} />
        <div>
          <Button type="submit" variant="secondaire" busy={busy}>
            {contractor ? "Enregistrer" : "Ajouter l’artisan"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

export function ContractorPage() {
  const { contractorId = "" } = useParams();
  const org = useOrg();
  const navigate = useNavigate();
  const q = useGet<any>(org.api(`/pro/contractors/${contractorId}`));
  const invalidate = useInvalidate();
  const { options: properties } = usePropertyOptions(org.orgId, "Aucun logement");
  const att = useRun();
  const inter = useRun();
  const [validUntil, setValidUntil] = useState("");
  const [form, setForm] = useState({ property_id: "", performed_on: "", description: "" });
  const [amount, setAmount] = useState<number | null>(null);
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorBox error={q.error} />;
  const c = q.data.contractor;
  return (
    <div className="page">
      <PageHeader
        eyebrow="Annuaire des artisans"
        title={c.name}
        intro={TRADES[c.trade] ?? c.trade}
        actions={
          org.can("operate") ? (
            <Button
              variant="discret"
              onClick={async () => {
                await del(org.api(`/pro/contractors/${c.id}`));
                invalidate(org.api("/pro"));
                navigate(org.to("professionnels/artisans"));
              }}
            >
              Archiver
            </Button>
          ) : null
        }
      />
      <Card title="Assurance décennale">
        <KeyValues
          items={[
            ["État", <Status kind="insurance" value={c.insurance_status} />],
            ["Assureur", c.insurer || "—"],
            ["Contrat", c.policy_number || "—"],
            ["Fin de validité lue sur la pièce", date(c.decennale_valid_until)],
            ["Attestation", c.decennale_document_id ? <a href={docUrl(c.decennale_document_id)} target="_blank" rel="noreferrer">Ouvrir l’attestation</a> : "Non déposée"],
          ]}
        />
        {org.can("operate") ? (
          <div className="stack">
            <Input label="Fin de validité indiquée sur la nouvelle attestation" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
            <FileDrop
              label="Déposer l’attestation"
              busy={att.busy}
              onFiles={async (files) => {
                const f = new FormData();
                f.append("file", files[0]);
                if (validUntil) f.append("valid_until", validUntil);
                if (await att.run(() => upload(org.api(`/pro/contractors/${c.id}/attestation`), f), "Attestation déposée.")) invalidate(org.api("/pro"));
              }}
            />
            <ErrorBox error={att.error} />
          </div>
        ) : null}
      </Card>
      <Card title="Interventions">
        {!q.data.interventions.length ? <p className="muted">Aucune intervention enregistrée.</p> : null}
        {q.data.interventions.length ? (
          <Table caption="Interventions">
            <thead>
              <tr>
                <th>Date</th>
                <th>Logement</th>
                <th>Description</th>
                <th className="num">Montant</th>
              </tr>
            </thead>
            <tbody>
              {q.data.interventions.map((i: any) => (
                <tr key={i.id}>
                  <td>{date(i.performed_on)}</td>
                  <td>{i.property_label ?? "—"}</td>
                  <td>{i.description}</td>
                  <td className="num">{i.amount_cents !== null ? money(i.amount_cents) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
        {org.can("operate") ? (
          <form
            className="stack"
            onSubmit={async (e) => {
              e.preventDefault();
              const body = { ...form, property_id: form.property_id || null, amount_cents: amount };
              if (await inter.run(() => post(org.api(`/pro/contractors/${c.id}/interventions`), body), "Intervention enregistrée.")) {
                setForm({ property_id: "", performed_on: "", description: "" });
                invalidate(org.api("/pro"));
              }
            }}
          >
            <h3>Enregistrer une intervention</h3>
            <div className="form-row">
              <Select label="Logement" value={form.property_id} options={properties} onChange={(e) => setForm({ ...form, property_id: e.target.value })} />
              <Input label="Date" type="date" required value={form.performed_on} onChange={(e) => setForm({ ...form, performed_on: e.target.value })} />
              <MoneyInput label="Montant" cents={amount} onChange={setAmount} />
            </div>
            <Input label="Description" required value={form.description} error={fieldError(inter.error, "description")} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <ErrorBox error={inter.error && !fieldError(inter.error, "description") ? inter.error : null} />
            <div>
              <Button type="submit" variant="secondaire" busy={inter.busy}>
                Enregistrer
              </Button>
            </div>
          </form>
        ) : null}
      </Card>
      {org.can("operate") ? <ContractorForm contractor={c} /> : null}
    </div>
  );
}
